"use client";

import { m as motion, AnimatePresence } from "framer-motion";
import { 
  ChevronLeft, 
  Download, 
  ExternalLink, 
  Mail, 
  Linkedin, 
  Github, 
  Globe, 
  Smartphone, 
  Code2, 
  GraduationCap, 
  Briefcase,
  FileText,
  MapPin,
  Calendar,
  Layers,
  Sparkles,
  ArrowUpRight,
  CheckCircle2
} from "lucide-react";
import Link from "next/link";
import { 
  HERO_DATA, 
  EXPERIENCE_DATA, 
  EDUCATION_DATA, 
  SKILLS_DATA,
  CONTACT_DATA
} from "@/lib/data";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { BackgroundLines } from "@/components/ui/background-lines";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { usePrefersReducedMotion } from "@/lib/performance-hooks";

interface ResumePageClientProps {
  resumeData?: any;
}

export default function ResumePageClient({
  resumeData,
}: ResumePageClientProps) {
  // Defaults to "details" (not "preview") so the actual resume text - experience,
  // education, skills - is what search engines and first paint see, instead of
  // an embedded PDF iframe with almost no crawlable text. Users can still switch
  // to the PDF view via the toggle button.
  const [activeTab, setActiveTab] = useState<"preview" | "details">("details");
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const downloadFileName = "Sachin_Sharma_Resume.pdf";
  const downloadText = "Download PDF";

  const prefersReducedMotion = usePrefersReducedMotion();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-background relative selection:bg-primary/30">
      <BackgroundLines />
      
      {/* Header - Glass Effect */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              href="/"
              className="p-2 hover:bg-secondary rounded-full transition-colors group"
            >
              <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            </Link>
            <div>
              <h1 className="font-black text-sm sm:text-base md:text-lg tracking-tight">Curriculum Vitae</h1>
              <p className="text-[10px] sm:text-xs text-muted-foreground font-black uppercase tracking-[0.2em] hidden sm:block">
                Sachin Sharma • Software Developer
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <div className="hidden md:flex items-center bg-secondary/50 p-1 rounded-full border border-border/50">
              <button
                onClick={() => setActiveTab("preview")}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-medium transition-all",
                  activeTab === "preview" 
                    ? "bg-primary text-primary-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Interactive
              </button>
              <button
                onClick={() => setActiveTab("details")}
                className={cn(
                  "px-4 py-1.5 rounded-full text-xs font-medium transition-all",
                  activeTab === "details" 
                    ? "bg-primary text-primary-foreground shadow-sm" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Information
              </button>
            </div>

            <div className="h-6 w-px bg-border hidden md:block mx-1" />
            
            <a
              href="/resume.pdf"
              download={downloadFileName}
              className="flex items-center gap-2 px-4 py-2 bg-foreground text-background dark:bg-primary dark:text-primary-foreground rounded-full text-xs md:text-sm font-bold hover:scale-105 active:scale-95 transition-all shadow-lg shadow-primary/20"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{downloadText}</span>
              <span className="sm:hidden">PDF</span>
            </a>
            
            <div className="hidden sm:block">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-20 px-4 md:px-8 max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          {activeTab === "preview" ? (
            <motion.div
              key="interactive"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
              className="w-full flex flex-col items-center gap-8"
            >
              {/* Premium PDF Shadow Wrapper */}
              <div className="relative w-full max-w-4xl group">
                {/* Decorative background elements */}
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-purple-500/10 to-blue-500/20 blur-3xl opacity-20 group-hover:opacity-40 transition-opacity rounded-[3rem]" />
                
                <div className="relative aspect-[1/1.414] w-full bg-[#fafafa] dark:bg-zinc-900 rounded-2xl md:rounded-3xl border border-white/20 shadow-2xl overflow-hidden ring-1 ring-black/5">
                  {/* Iframe Loading State Placeholder */}
                  <div className="absolute inset-0 flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 animate-pulse z-0">
                    <FileText className="w-12 h-12 text-muted-foreground/20" />
                  </div>
                  
                  {isHydrated && (
                    <iframe
                      src="/resume.pdf#toolbar=0&navpanes=0&scrollbar=0"
                      className="relative z-10 w-full h-full border-none pointer-events-auto"
                      title="Sachin Sharma Resume"
                    />
                  )}

                  {/* External Link Overlay (Top Right) */}
                  <div className="absolute top-4 right-4 z-20 flex gap-2">
                    <a
                      href="/resume.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white hover:bg-white/20 transition-all shadow-xl"
                      title="Open full PDF"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Mobile optimization hint */}
                <div className="mt-6 text-center md:hidden">
                  <p className="text-xs text-muted-foreground animate-pulse flex items-center justify-center gap-2 font-medium">
                    <Sparkles className="w-3 h-3" /> Scroll for detailed experience
                  </p>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="details"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12"
            >
              {/* Sidebar - Personal Info */}
              <div className="lg:col-span-4 space-y-6 md:space-y-8">
                <motion.div variants={itemVariants} className="space-y-5 md:space-y-6">
                  <div className="space-y-3 sm:space-y-4 text-center lg:text-left">
                    <h2 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tighter">
                      {HERO_DATA.name}
                    </h2>
                    <p className="text-primary font-black tracking-[0.2em] sm:tracking-[0.3em] text-[10px] sm:text-sm uppercase">
                      {HERO_DATA.headline}
                    </p>
                  </div>

                  <p className="text-muted-foreground/80 font-medium leading-relaxed text-xs sm:text-base md:text-lg text-center lg:text-left px-2 sm:px-0">
                    {HERO_DATA.description}
                  </p>

                  <div className="flex flex-col gap-3.5 items-center lg:items-start">
                    <div className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                        <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                      </div>
                      <span className="text-[13px] sm:text-sm font-medium">{CONTACT_DATA.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-muted-foreground hover:text-foreground transition-colors group">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                        <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                      </div>
                      <span className="text-[13px] sm:text-sm font-medium">{CONTACT_DATA.location}</span>
                    </div>
                  </div>

                  <div className="flex justify-center lg:justify-start gap-2.5 pt-1">
                    {HERO_DATA.socialLinks?.map((social) => {
                      const Icon = social.name === 'github' ? Github : social.name === 'linkedin' ? Linkedin : Mail;
                      return (
                        <a
                          key={social.name}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2.5 sm:p-3 bg-secondary/80 rounded-xl sm:rounded-2xl hover:bg-primary hover:text-primary-foreground transition-all group shadow-sm"
                        >
                          <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                        </a>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Skills Grid */}
                <motion.div variants={itemVariants} className="space-y-6 pt-4">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" />
                    <h3 className="font-black text-xs uppercase tracking-[0.2em]">Technical Stack</h3>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {SKILLS_DATA.flatMap(cat => cat.skills).slice(0, 15).map((skill) => (
                      <span 
                        key={skill.name}
                        className="px-3 py-1.5 bg-secondary/50 rounded-lg text-xs font-bold border border-border/50 hover:border-primary/50 transition-colors"
                      >
                        {skill.name}
                      </span>
                    ))}
                    <span className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-black uppercase tracking-widest">
                      & More
                    </span>
                  </div>
                </motion.div>
              </div>

              {/* Main Column - Experience & Education */}
              <div className="lg:col-span-8 space-y-12">
                {/* Experience */}
                <section className="space-y-10">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-black tracking-tight">Professional Experience</h3>
                      <div className="h-1 w-12 bg-primary rounded-full mt-1" />
                    </div>
                  </div>

                  <div className="relative space-y-12 pl-4 md:pl-0">
                    {/* Vertical Line */}
                    <div className="absolute left-[7px] md:left-[19px] top-4 bottom-0 w-px bg-border/50 border-l border-dashed border-border" />

                    {EXPERIENCE_DATA.map((exp, idx) => (
                      <motion.div 
                        key={idx}
                        variants={itemVariants}
                        className="relative pl-10 md:pl-16 group"
                      >
                        {/* Dot */}
                        <div className="absolute left-0 md:left-[12px] top-1.5 w-4 h-4 rounded-full border-2 border-primary bg-background group-hover:scale-125 transition-transform z-10 shadow-[0_0_10px_rgba(var(--primary),0.3)]" />
                        
                        <div className="space-y-4">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 md:gap-4">
                            <div>
                              <h4 className="font-black text-lg sm:text-xl md:text-2xl group-hover:text-primary transition-colors duration-300">
                                {exp.role}
                              </h4>
                              <p className="font-bold text-muted-foreground/80 flex items-center gap-2 text-sm sm:text-base">
                                {exp.company}
                                <span className="w-1.5 h-1.5 rounded-full bg-border" />
                                <span className="text-primary/70 text-[10px] sm:text-xs tracking-widest uppercase font-black">{exp.type}</span>
                              </p>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-1 bg-secondary rounded-full text-[10px] md:text-xs font-bold w-fit shadow-sm">
                              <Calendar className="w-3 h-3" />
                              {exp.period}
                            </div>
                          </div>

                          <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                            {exp.description}
                          </p>

                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
                            {exp.achievements.map((ach, i) => (
                              <li key={i} className="flex items-start gap-4 text-sm sm:text-base font-medium text-muted-foreground/80">
                                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                <span>{ach}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </section>

                {/* Education */}
                <section className="space-y-8 pt-8 border-t border-border/50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-black tracking-tight">Academic Journey</h3>
                      <div className="h-1 w-12 bg-purple-500 rounded-full mt-1" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {EDUCATION_DATA.map((edu, idx) => (
                      <motion.div 
                        key={idx}
                        variants={itemVariants}
                        className="p-6 bg-secondary/30 rounded-3xl border border-border/50 hover:border-purple-500/30 transition-all group h-full flex flex-col justify-between"
                      >
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="w-10 h-10 rounded-xl bg-background border border-border flex items-center justify-center font-black text-purple-500 shadow-sm">
                              {edu.logo}
                            </div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                              {edu.year}
                            </span>
                          </div>
                          <div className="space-y-1">
                            <h4 className="font-black text-lg leading-tight group-hover:text-purple-500 transition-colors">
                              {edu.school}
                            </h4>
                            <p className="text-sm font-bold text-muted-foreground">
                              {edu.degree}
                            </p>
                          </div>
                        </div>
                        {edu.grade && (
                          <div className="mt-6 pt-4 border-t border-border/30">
                            <span className="text-xs font-black text-purple-500/70 uppercase tracking-[0.2em]">Result: </span>
                            <span className="text-sm font-black">{edu.grade}</span>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </section>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Floating Action Button for Mobile PDF */}
      <div className="fixed bottom-6 right-6 z-50 md:hidden scale-110">
        <a
          href="/resume.pdf"
          target="_blank"
          className="flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground rounded-2xl shadow-2xl animate-bounce"
        >
          <ExternalLink className="w-6 h-6" />
        </a>
      </div>

      {/* Bottom Nav Hint */}
      <div className="md:hidden fixed bottom-6 left-6 z-50">
        <button
          onClick={() => setActiveTab(activeTab === "preview" ? "details" : "preview")}
          className="flex items-center gap-2 px-5 py-3 bg-background/90 backdrop-blur-md border border-border rounded-2xl shadow-xl text-xs font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all"
        >
          <ArrowUpRight className="w-4 h-4 text-primary" />
          {activeTab === "preview" ? "View Details" : "View PDF"}
        </button>
      </div>
    </div>
  );
}
